import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { questionDefinitions, STEP_TITLES } from './questions';
import { FormData } from './schema';
import { safeGetItem } from './data/storage';

const AGENT_WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '';

interface ApplicantMeta {
  id: string;
  name: string;
}

const loadApplicantList = (): ApplicantMeta[] => {
  const list = safeGetItem('visaApplicantList');
  return list ? JSON.parse(list) : [{ id: 'app_1', name: 'Applicant #1' }];
};

const loadFormDataForApplicant = (id: string): FormData => {
  const raw = safeGetItem(`visaFormData_${id}`);
  return raw ? JSON.parse(raw) : {};
};

function Review() {
  const navigate = useNavigate();
  const [applicants] = useState<ApplicantMeta[]>(loadApplicantList);
  const [activeApplicantId, setActiveApplicantId] = useState<string>(applicants[0]?.id || 'app_1');
  const [data, setData] = useState<FormData>(() => loadFormDataForApplicant(activeApplicantId));
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setData(loadFormDataForApplicant(activeApplicantId));
  }, [activeApplicantId]);

  const buildText = () => {
    const lines: string[] = [];
    questionDefinitions.forEach((q) => {
      if (q.isHeading || q.isSubHeading) return;
      const value = data[q.id as keyof FormData];
      if (value !== undefined && value !== null && value !== '') {
        const label = q.label.replace(/\s+/g, ' ').trim();
        let displayValue = value;
        if (Array.isArray(value)) {
          displayValue = value.join(', ');
        }
        lines.push(`${label}: ${displayValue}`);
      }
    });
    return lines.join('\n');
  };

  const fullText = buildText();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(fullText);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const shareOnWhatsApp = () => {
    const encodedText = encodeURIComponent(
      `✅ *New Visa Application Details*\n\n${fullText}\n\n---\nPlease process this application.`
    );
    let url = `https://wa.me/`;
    if (AGENT_WHATSAPP_NUMBER) {
      url += AGENT_WHATSAPP_NUMBER;
    }
    url += `?text=${encodedText}`;
    window.open(url, '_blank');
  };

  const handlePrintPDF = () => {
    window.print();
  };

  const handleEditDetails = () => {
    const params = new URLSearchParams(window.location.search);
    const country = (params.get('country') || '').toUpperCase();
    if (country === 'MALAYSIA') {
      navigate('/malaysia');
    } else if (country === 'VIETNAM') {
      navigate('/vietnam');
    } else {
      navigate('/india');
    }
  };

  return (
    <div style={{ maxWidth: '940px', margin: '0 auto', padding: '2rem 1rem 4rem' }}>
      {/* ===== PRINT STYLES ===== */}
      <style>{`
        @media print {
          body { background: #ffffff !important; color: #000000 !important; }
          .no-print { display: none !important; }
          .card-container { border: 1px solid #000000 !important; box-shadow: none !important; margin-bottom: 1rem !important; }
          header { background: #1e3a8a !important; color: #ffffff !important; -webkit-print-color-adjust: exact; }
        }
      `}</style>

      {/* ===== HEADER ===== */}
      <div
        style={{
          background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
          borderRadius: '16px',
          padding: '2rem 1.75rem',
          color: '#ffffff',
          boxShadow: '0 10px 25px -5px rgba(16, 185, 129, 0.3)',
          marginBottom: '2rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <span
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                padding: '0.35rem 0.85rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: '600',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              Submission Summary
            </span>
            <h1 style={{ fontSize: '1.85rem', fontWeight: '800', marginTop: '0.5rem', margin: 0 }}>
              ✅ Application Summary Dashboard
            </h1>
            <p style={{ color: '#a7f3d0', fontSize: '0.9rem', marginTop: '0.25rem' }}>
              Exclusively designed for <strong>mySky Travels</strong> by{' '}
              <a
                href="https://www.madushka.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#ffffff', fontWeight: '700', textDecoration: 'underline' }}
              >
                Madushka Athapaththu
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* ===== APPLICANT TABS ===== */}
      {applicants.length > 1 && (
        <div className="no-print" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', overflowX: 'auto' }}>
          {applicants.map((app) => {
            const isActive = app.id === activeApplicantId;
            return (
              <button
                key={app.id}
                onClick={() => setActiveApplicantId(app.id)}
                style={{
                  padding: '0.6rem 1.1rem',
                  borderRadius: '8px',
                  border: isActive ? '2px solid #059669' : '1px solid #cbd5e1',
                  background: isActive ? '#ecfdf5' : '#ffffff',
                  color: isActive ? '#047857' : '#475569',
                  fontWeight: isActive ? '700' : '500',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                }}
              >
                👤 {app.name}
              </button>
            );
          })}
        </div>
      )}

      {/* ===== ACTION CONTROLS ===== */}
      <div className="no-print" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
        <button
          onClick={shareOnWhatsApp}
          className="btn-primary"
          style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', flex: '1 1 200px' }}
        >
          💬 Share via WhatsApp
        </button>

        <button
          onClick={handlePrintPDF}
          className="btn-primary"
          style={{ background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', flex: '1 1 180px' }}
        >
          🖨️ Download PDF / Print
        </button>

        <button
          onClick={copyToClipboard}
          className="btn-secondary"
          style={{ flex: '1 1 160px', background: copied ? '#dcfce7' : '#ffffff', color: copied ? '#15803d' : '#1e293b' }}
        >
          {copied ? '✅ Copied!' : '📋 Copy Text'}
        </button>

        <button onClick={handleEditDetails} className="btn-secondary" style={{ flex: '0 0 auto' }}>
          ✏️ Edit Details
        </button>
      </div>

      {/* ===== CATEGORIZED SUMMARY BY STEPS ===== */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {STEP_TITLES.map((st) => {
          const stepQuestions = questionDefinitions.filter(
            (q) => q.step === st.step && !q.isHeading && !q.isSubHeading
          );

          const stepData = stepQuestions.filter((q) => {
            const val = data[q.id as keyof FormData];
            return val !== undefined && val !== null && val !== '';
          });

          if (stepData.length === 0) return null;

          return (
            <div key={st.step} className="card-container" style={{ padding: '1.5rem', marginBottom: 0 }}>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span>{st.icon}</span> Step {st.step}: {st.title}
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                {stepData.map((q) => {
                  const val = data[q.id as keyof FormData];
                  const displayVal = Array.isArray(val) ? val.join(', ') : String(val);

                  return (
                    <div
                      key={q.id}
                      style={{
                        padding: '0.75rem 1rem',
                        background: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #f1f5f9',
                      }}
                    >
                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>
                        {q.label.replace(/^\*/, '')}
                      </div>
                      <div style={{ fontSize: '0.95rem', color: '#0f172a', fontWeight: '500', marginTop: '0.2rem', wordBreak: 'break-word' }}>
                        {displayVal}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== RAW TEXT DISPLAY ===== */}
      <div className="card-container no-print" style={{ marginTop: '2rem', padding: '1.5rem' }}>
        <h4 style={{ fontSize: '1rem', color: '#475569', marginBottom: '0.75rem' }}>Raw Form Output</h4>
        <pre
          style={{
            background: '#0f172a',
            color: '#38bdf8',
            padding: '1.25rem',
            borderRadius: '10px',
            fontSize: '0.85rem',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: '300px',
            overflow: 'auto',
          }}
        >
          {fullText || 'No data found. Please complete the application form.'}
        </pre>
      </div>

      {/* ===== FOOTER ===== */}
      <footer
        style={{
          marginTop: '3rem',
          paddingTop: '1.5rem',
          borderTop: '1px solid #e2e8f0',
          textAlign: 'center',
          color: '#64748b',
          fontSize: '0.8rem',
        }}
      >
        <p>© 2026 <strong>mySky Travels</strong>. All rights reserved.</p>
        <p style={{ marginTop: '0.25rem' }}>
          Designed exclusively for mySky Travels by <strong>Madushka Athapaththu</strong>.
        </p>
      </footer>
    </div>
  );
}

export default Review;